import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading, adminStatus, isAdminMember, signOut } = useAuth();
  const location = useLocation();

  // Wait for both the session probe AND the admin_users lookup before
  // making a gate decision — otherwise a signed-in viewer would briefly
  // flash the "access denied" card before adminStatus settles.
  if (loading || (session && adminStatus === 'unknown')) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Loading session…
      </div>
    );
  }
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // A valid Supabase user who is NOT in harvest.admin_users gets past the
  // session check but would then hit 0-row RLS-filtered queries everywhere
  // and see <ErrorState> in every panel. Surface the gate explicitly so the
  // user has an actionable next step instead of a half-broken dashboard.
  if (!isAdminMember) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-warning" />
              <CardTitle className="text-base">Access denied</CardTitle>
            </div>
            <CardDescription>
              You signed in successfully, but your account is not in the{' '}
              <span className="font-mono">harvest.admin_users</span> roster.
              Ask the platform owner to add you (with role <code>admin</code>{' '}
              for write access or <code>viewer</code> for read-only).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" onClick={() => void signOut()}>
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  return <>{children}</>;
}
